
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Fund } from "@/types/fund";
import { logger } from "@/services/LoggingService";
import { updateFundImage } from "./mutations/updateFundImage";
import {
  getFundQueryKey,
  handleOptimisticUpdate,
  revertOptimisticUpdate
} from "./mutations/fundImageCache";

const COMPONENT = 'FundImageMutation';

export function useFundImageMutation(fundId: string) {
  const queryClient = useQueryClient();
  const queryKey = getFundQueryKey(fundId);

  return useMutation({
    mutationFn: (imageUrl: string | null) => updateFundImage(fundId, imageUrl),
    
    onMutate: async (newImageUrl) => {
      return handleOptimisticUpdate(queryClient, fundId, newImageUrl);
    },
    
    onError: (error, _, context) => {
      logger.error(COMPONENT, 'Mutation failed', {
        error: error.message,
        fundId,
        context: context ? 'Has previous data' : 'No previous data'
      });

      if (context?.previousData) {
        revertOptimisticUpdate(queryClient, fundId, context.previousData);
      }
      
      toast.error(error.message || "Failed to update fund image");
    },
    
    onSuccess: (data) => {
      logger.info(COMPONENT, 'Mutation completed successfully', {
        fundId,
        hasData: !!data
      });

      queryClient.invalidateQueries({ queryKey });
      toast.success("Fund image updated successfully");
    },
    
    onSettled: () => {
      logger.debug(COMPONENT, 'Mutation settled', { fundId });
    }
  });
}
